/**
 * API base URL for fetch calls.
 * Priority: REACT_APP_LOCAL_API_URL → localhost default (8899) → REACT_APP_API_URL
 */
export function getApiBaseUrl() {
  const local = process.env.REACT_APP_LOCAL_API_URL;
  if (local) {
    return local.replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://127.0.0.1:8899';
    }
  }

  const remote = process.env.REACT_APP_API_URL;
  if (!remote) {
    return '';
  }
  return remote.replace(/\/+$/, '');
}

/** Layout-only preview when API/DB unavailable (default on localhost unless explicitly disabled). */
export function isSignonUiPreviewEnabled() {
  if (process.env.REACT_APP_SIGNON_UI_PREVIEW === 'false') {
    return false;
  }
  if (process.env.REACT_APP_SIGNON_UI_PREVIEW === 'true') {
    return true;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1';
  }
  return false;
}

export function getRemoteApiBaseUrl() {
  const remote = process.env.REACT_APP_API_URL;
  return remote ? remote.replace(/\/+$/, '') : '';
}

/** Player sign-on API paths (under api/player_signon/). */
export const PLAYER_SIGNON_PATHS = {
  getTeam: '/player_signon/get_public_team.php',
  checkParent: '/player_signon/check_parent.php',
  submit: '/player_signon/submit.php',
};

/** Try local API, then remote, then preview fallback. */
export async function fetchPublicTeamSignon(teamId) {
  const bases = [];
  const local = getApiBaseUrl();
  const remote = getRemoteApiBaseUrl();
  if (local) bases.push(local);
  if (remote && remote !== local) bases.push(remote);

  for (const base of bases) {
    try {
      const res = await fetch(`${base}${PLAYER_SIGNON_PATHS.getTeam}?t=${teamId}`);
      const data = await res.json();
      if (data.success && data.team) {
        return { team: data.team, source: base };
      }
    } catch (e) {
      // try next base
    }
  }

  if (isSignonUiPreviewEnabled()) {
    return { team: getSignonPreviewTeam(teamId), source: 'preview' };
  }
  return { team: null, source: null };
}

export function getSignonPreviewTeam(teamId) {
  return {
    team_id: teamId,
    team_name: 'Eastbourne United AFC',
    type: 'academy',
    age_group: 'U12s',
    age_group_label: 'U12s',
    location: 'Eastbourne',
    county: 'East Sussex',
  };
}

/** API bases for sign-on (local first, then remote). */
export function getSignonApiBases() {
  const bases = [];
  const local = process.env.REACT_APP_LOCAL_API_URL;
  if (local) {
    bases.push(local.replace(/\/+$/, ''));
  } else if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      bases.push('http://127.0.0.1:8899');
    }
  }
  const remote = getRemoteApiBaseUrl();
  if (remote && !bases.includes(remote)) {
    bases.push(remote);
  }
  const primary = getApiBaseUrl();
  if (primary && !bases.includes(primary)) {
    bases.push(primary);
  }
  return bases;
}

/** Whether any API base is configured for sign-on requests. */
export function hasSignonApiBases() {
  return getSignonApiBases().length > 0;
}

/** POST parent lookup for sign-on step 1 (existing account by mobile). */
export async function checkParentSignon(body) {
  const bases = getSignonApiBases();
  let lastMessage = 'Could not reach the sign-on API.';

  for (const base of bases) {
    try {
      const res = await fetch(`${base}${PLAYER_SIGNON_PATHS.checkParent}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        return { success: true, data, base };
      }
      lastMessage = data.message || lastMessage;
    } catch (e) {
      // try next base
    }
  }

  return { success: false, message: lastMessage };
}

/** POST sign-on; tries each API base until one succeeds. */
export async function submitPlayerSignon(body) {
  const bases = getSignonApiBases();
  let lastMessage = 'Could not reach the sign-on API.';

  for (const base of bases) {
    try {
      const res = await fetch(`${base}${PLAYER_SIGNON_PATHS.submit}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        return { success: true, data, base };
      }
      lastMessage = data.message || lastMessage;
    } catch (e) {
      // try next base
    }
  }

  return { success: false, message: lastMessage };
}
